import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  HeartHandshake, ShieldCheck, MessagesSquare, CalendarCheck,
  Star, Quote, ChevronDown, Globe, Lock, UserCheck, Sparkles
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { formatXOF } from "@/lib/api";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1739302750675-042ed497a429?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const DEFAULT_HOW = "/assets/elegant-senegalese-woman.png";

function resolveUrl(u, fallback) {
  if (!u) return fallback;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function StepCard({ n, icon: Icon, title, text }) {
  return (
    <div className="card-soft card-soft-hover" data-testid={`step-card-${n}`}>
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 rounded-full bg-sage-50 text-sage-600 font-display text-xl border border-sage-100">{n}</span>
        <Icon className="w-5 h-5 text-gold-dark" strokeWidth={1.5} />
      </div>
      <h3 className="font-display text-2xl mt-4 text-ink">{title}</h3>
      <p className="text-ink-soft mt-2 leading-relaxed text-[15px]">{text}</p>
    </div>
  );
}

export default function Landing() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const heroImg = resolveUrl(settings.hero_image_url, DEFAULT_HERO);
  const howImg = resolveUrl(settings.how_image_url, DEFAULT_HOW);
  const waHref = `https://wa.me/${(settings.whatsapp_number || "15142389008").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Bonjour " + (settings.app_name || "Sama Écoute"))}`;
  const accVisible = !!settings.accompagnateurs_visible;
  const bookingHref = accVisible
    ? "/accompagnateurs"
    : settings.primary_accompagnateur_id
      ? `/reservation/${settings.primary_accompagnateur_id}`
      : "/accompagnateurs";
  const [accompagnateurs, setAccompagnateurs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/accompagnateurs"),
          api.get("/testimonials"),
        ]);
        setAccompagnateurs(a.data.items || []);
        setTestimonials(t.data.items || []);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  const onContactSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const message = (fd.get("message") || "").toString().trim();
    if (message.length < 5) {
      toast.error("Votre message doit contenir au moins 5 caractères.");
      return;
    }
    try {
      await api.post("/contact", {
        name: fd.get("name"),
        email: fd.get("email"),
        subject: fd.get("subject"),
        message,
      });
      toast.success("Message envoyé. Nous revenons vers vous très vite.");
      e.target.reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const reason = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.message).join(" · ")
        : (typeof detail === "string" ? detail : null);
      toast.error(reason || "Impossible d'envoyer le message. Réessayez.");
    }
  };

  return (
    <div className="bg-bone">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-sage-100/60 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-[36rem] h-[36rem] rounded-full bg-gold-light/40 blur-3xl" />
        </div>
        <div className="container-jamm pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="overline mb-5">Sénégal · Afrique · Diaspora · Partout</div>
            <h1 className="h-display">
              Parlez librement,<br />
              <span className="italic text-sage-600">sans jugement.</span>
            </h1>
            <p className="lead mt-6 max-w-xl">
              Sama Écoute, c'est l'écoute humaine que vous méritez. Réservez une séance privée et confidentielle avec un accompagnateur qui comprend votre culture, votre langue et votre histoire.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to={bookingHref} className="btn-primary" data-testid="hero-book-btn">
                <CalendarCheck className="w-5 h-5" /> Prendre rendez-vous
              </Link>
              <a
                href={waHref}
                target="_blank" rel="noreferrer"
                className="btn-secondary"
                data-testid="hero-chat-btn"
              >
                <MessagesSquare className="w-5 h-5" /> Discuter maintenant
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-ink-soft">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sage-500" /> 100% confidentiel</span>
              <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-sage-500" /> Mode anonyme disponible</span>
              <span className="flex items-center gap-2"><Globe className="w-4 h-4 text-sage-500" /> Français · Wolof · Anglais</span>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-in">
            <div className="relative">
              <img
                src={heroImg}
                alt="Femmes africaines en confiance"
                className="rounded-3xl object-cover w-full h-[460px] lg:h-[560px] shadow-[0_24px_60px_rgba(26,36,33,0.12)]"
              />
              <div className="absolute -left-6 bottom-8 hidden md:flex glass rounded-2xl px-5 py-4 shadow-lg w-72">
                <div className="flex items-center gap-3">
                  <div className="grid place-items-center w-10 h-10 rounded-full bg-sage-500 text-white">
                    <HeartHandshake className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display text-lg leading-none text-ink">+ 1 700</div>
                    <div className="text-xs text-ink-soft mt-1">séances d'écoute</div>
                  </div>
                </div>
              </div>
              <div className="absolute -right-4 -top-4 hidden md:flex glass rounded-full px-4 py-2 items-center gap-2 shadow">
                <Star className="w-4 h-4 text-gold-dark" fill="currentColor" />
                <span className="text-sm text-ink font-medium">4.9 / 5</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA FONCTIONNE */}
      <section className="section" data-testid="how-section">
        <div className="container-jamm">
          <div className="max-w-2xl">
            <div className="overline">Comment ça fonctionne</div>
            <h2 className="h-section mt-4">Trois étapes, une parole libérée.</h2>
            <p className="lead mt-4">Pas de protocole médical. Pas de dossier. Juste une conversation humaine, organisée simplement.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <StepCard n="1" icon={UserCheck} title="Choisissez un accompagnateur" text="Parcourez les profils, lisez les histoires, choisissez la personne avec qui vous vous sentez en confiance." />
            <StepCard n="2" icon={CalendarCheck} title="Réservez une séance" text="15, 30 ou 60 minutes. Choisissez la date, l'heure, en visio ou en audio. Mode anonyme disponible." />
            <StepCard n="3" icon={MessagesSquare} title="Parlez en toute sécurité" text="Connectez-vous depuis votre espace. Tout reste confidentiel — votre histoire vous appartient." />
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-8 items-center">
            <img src={howImg} alt="Espace d'écoute" className="rounded-3xl w-full h-[360px] object-cover" />
            <div>
              <h3 className="font-display text-3xl text-ink leading-tight">Un espace pour vous, où que vous soyez.</h3>
              <p className="lead mt-4">Mariage, parentalité, immigration, reconversion, deuil, charge mentale, doutes du quotidien… Que vous soyez à Dakar, Thiès, Saint-Louis, Paris, Montréal ou ailleurs — nos accompagnateurs vous comprennent <em>vraiment</em>. Pas de tabous.</p>
              <Link to={bookingHref} className="btn-outline mt-6" data-testid="how-cta-btn">
                {accVisible ? "Découvrir les accompagnateurs" : "Prendre rendez-vous"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ACCOMPAGNATEURS */}
      {accVisible && (
      <section className="section bg-beige/40" data-testid="accompagnateurs-section">
        <div className="container-jamm">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-xl">
              <div className="overline">Nos accompagnateurs</div>
              <h2 className="h-section mt-4">Des personnes vraies, pas des numéros.</h2>
              <p className="lead mt-4">Femmes, hommes, jeunes adultes, parents — chacun avec son vécu et son écoute.</p>
            </div>
            <Link to="/accompagnateurs" className="btn-outline" data-testid="acc-section-cta">Voir tous les profils</Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {accompagnateurs.slice(0, 6).map((a) => (
              <Link
                key={a.id}
                to={`/accompagnateurs/${a.id}`}
                className="card-soft card-soft-hover block group"
                data-testid={`acc-card-${a.id}`}
              >
                <div className="flex items-start gap-4">
                  <img src={a.avatar_url} alt={a.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-sage-100" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-xl text-ink">{a.name}</h3>
                      {a.verified && <span className="text-[10px] uppercase tracking-wider text-sage-600 bg-sage-50 px-2 py-0.5 rounded-full">Vérifié</span>}
                    </div>
                    <div className="text-ink-soft text-sm mt-0.5">{a.title}</div>
                    <div className="flex items-center gap-1 text-sm mt-1.5">
                      <Star className="w-4 h-4 text-gold-dark" fill="currentColor" />
                      <span className="font-medium text-ink">{a.rating}</span>
                      <span className="text-ink-mute text-xs">· {a.consultations} séances</span>
                    </div>
                  </div>
                </div>
                <p className="text-ink-soft mt-4 text-sm leading-relaxed line-clamp-3">{a.bio}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {a.specialties?.slice(0, 3).map((s) => (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{s}</span>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-ink-mute">à partir de</span>
                  <span className="font-display text-2xl text-sage-700">{formatXOF(a.rate_15)}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CONFIDENTIALITÉ */}
      <section className="section bg-ink text-bone" data-testid="confidentiality-section">
        <div className="container-jamm grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-gold mb-4">Confidentialité</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">Ce qui se dit ici, reste ici.</h2>
            <p className="text-bone/75 mt-5 text-lg leading-relaxed">
              Vos discussions sont chiffrées. Vous pouvez utiliser un pseudo, masquer votre photo, rejoindre les groupes en mode anonyme. Aucune donnée n'est revendue. Jamais.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, t: "Chiffrement", d: "Vos messages sont protégés de bout en bout." },
              { icon: Lock, t: "Mode anonyme", d: "Pseudo, photo masquée, présence invisible." },
              { icon: UserCheck, t: "Accompagnateurs vérifiés", d: "Profils contrôlés et engagés à la confidentialité." },
              { icon: Sparkles, t: "Suppression simple", d: "Effacez votre compte et vos données quand vous voulez." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition">
                <Icon className="w-6 h-6 text-gold mb-3" strokeWidth={1.5} />
                <div className="font-display text-xl text-bone">{t}</div>
                <div className="text-bone/60 text-sm mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="section" data-testid="testimonials-section">
        <div className="container-jamm">
          <div className="overline">Témoignages</div>
          <h2 className="h-section mt-4 max-w-2xl">Des paroles. Des respirations. Des vies allégées.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {testimonials.map((t, i) => (
              <div key={i} className="card-soft card-soft-hover" data-testid={`testimonial-${i}`}>
                <Quote className="w-6 h-6 text-gold-dark" strokeWidth={1.5} />
                <p className="text-ink mt-4 leading-relaxed font-display text-xl">“{t.quote}”</p>
                <div className="flex items-center gap-3 mt-6">
                  <img src={t.avatar_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="font-medium text-ink text-sm">{t.name}</div>
                    <div className="text-xs text-ink-mute">{t.country}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TARIFS — section retirée du Landing : la page /tarifs (zone-based) est la source unique */}

      {/* FAQ */}
      <section id="faq" className="section" data-testid="faq-section">
        <div className="container-jamm grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline">FAQ</div>
            <h2 className="h-section mt-4">Vos questions, nos réponses.</h2>
            <p className="lead mt-4">Si vous ne trouvez pas votre réponse, écrivez-nous — on vous répond toujours.</p>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {[
                { q: "Sama Écoute est-elle une plateforme médicale ?", a: "Non. Sama Écoute est un espace d'écoute, d'orientation et de soutien moral. Nos accompagnateurs ne sont pas des thérapeutes, ils sont des personnes formées à l'écoute bienveillante." },
                { q: "Mes informations sont-elles confidentielles ?", a: "Oui. Vos discussions sont chiffrées et nous ne partageons aucune donnée. Vous pouvez utiliser un pseudo et un mode anonyme." },
                { q: "Puis-je annuler ou reporter une séance ?", a: "Oui, jusqu'à 4 heures avant. Sans frais, sans justification." },
                { q: "Comment se passe le paiement ?", a: "Le paiement est sécurisé. Wave, Orange Money, carte bancaire et PayPal seront supportés. Pour le moment, vous pouvez réserver et nous vous contactons pour le règlement." },
                { q: "Quelles langues sont disponibles ?", a: "Le français par défaut, et selon les accompagnateurs : wolof, anglais. D'autres langues arriveront." },
              ].map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`} className="border-b border-border/60">
                  <AccordionTrigger className="text-left font-display text-xl text-ink hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-soft leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section bg-beige/40" data-testid="contact-section">
        <div className="container-jamm grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline">Contact</div>
            <h2 className="h-section mt-4">Une question ? Écrivez-nous.</h2>
            <p className="lead mt-4">Notre équipe vous répond avec attention. Pour les urgences, utilisez WhatsApp et formulaires ou courriel.</p>
            <div className="mt-8 space-y-3 text-ink-soft">
              <div data-testid="contact-location">📍 {settings.contact_location || "Sénégal & Diaspora — Disponible"}</div>
              <div data-testid="contact-email-line">✉️ <a href={`mailto:${settings.contact_email}`} className="hover:text-sage-600 transition">{settings.contact_email}</a></div>
            </div>
            <a
              href={`https://wa.me/${(settings.whatsapp_number || "15142389008").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Bonjour " + (settings.app_name || "Sama Écoute"))}`}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold hover:bg-[#1bb056] transition shadow-md"
              data-testid="contact-whatsapp-btn"
            >
              <MessagesSquare className="w-4 h-4" strokeWidth={2} /> WhatsApp · +{(settings.whatsapp_number || "15142389008").replace(/[^0-9]/g, "").replace(/^(\d)(\d{3})(\d{3})(\d{4})$/, "$1 $2 $3 $4")}
            </a>
          </div>
          <form onSubmit={onContactSubmit} className="card-soft" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="name" placeholder="Votre nom" className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="contact-name" />
              <input required type="email" name="email" placeholder="Email" className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="contact-email" />
            </div>
            <input name="subject" placeholder="Sujet (optionnel)" className="mt-4 w-full rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="contact-subject" />
            <textarea required minLength={5} name="message" rows={5} placeholder="Votre message (5 caractères minimum)…" className="mt-4 w-full rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="contact-message" />
            <button type="submit" className="btn-primary mt-5" data-testid="contact-submit">
              Envoyer le message
            </button>
          </form>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section" data-testid="final-cta-section">
        <div className="container-jamm">
          <div className="rounded-3xl bg-sage-500 text-white px-8 py-16 md:p-16 grid md:grid-cols-2 gap-8 items-center relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-gold/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-4xl md:text-5xl leading-tight">Vous n'êtes pas seul·e.</h2>
              <p className="text-white/85 mt-4 text-lg max-w-md">Un accompagnateur est prêt à vous écouter. Faites le premier pas — il change tout.</p>
            </div>
            <div className="relative flex flex-wrap gap-3 md:justify-end">
              <Link to={bookingHref} className="btn-pill bg-white text-sage-700 px-7 py-3 hover:bg-white/95 hover:-translate-y-0.5 font-medium" data-testid="final-book-btn">
                {accVisible ? "Trouver mon accompagnateur" : "Réserver une séance"}
              </Link>
              <Link to="/register" className="btn-pill bg-ink text-white px-7 py-3 hover:bg-ink/90 hover:-translate-y-0.5 font-medium" data-testid="final-register-btn">
                Créer un compte
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
